import ApplicationController from "application_controller.js";
import * as easepick from "@gothassos/easepick-bundle";
window.easepick = easepick; //* fixes issues loading plugins

//* Display-only calendar for the public event modal. It highlights the event's
//* upcoming dates (green = active, red strikethrough = canceled) and rings the
//* date currently being viewed. It is purely presentational: days are not
//* clickable, only month navigation works. Unlike the admin
//* `event-dates-calendar` controller, it never writes any form inputs.
export default class extends ApplicationController {
  debug = false;

  static targets = ["calendar"];

  static values = {
    dates: { type: Array, default: [] },
    locale: { type: String, default: "en-US" },
    css: { type: String, default: "https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.0/dist/index.css" },
  };

  connect() {
    this.log_function("connect");

    //* Map<"YYYY-MM-DD", { status: boolean, current: boolean }>
    this.dates = new Map();
    this.datesValue.forEach((d) => {
      if (!d.date) return;
      this.dates.set(d.date, { status: d.status !== false, current: d.current === true });
    });

    //* Navigation is bounded to the months that actually hold a date, so months
    //* without events (e.g. the current month when all dates are upcoming) are
    //* not reachable.
    const monthValue = (date) => date.getFullYear() * 12 + date.getMonth();
    const dateMonths = [...this.dates.keys()].map((k) => monthValue(this._parse(k)));
    this.minMonth = Math.min(...dateMonths);
    this.maxMonth = Math.max(...dateMonths);

    //* Open on the first month that has a date: the earliest upcoming date, or
    //* the earliest of all dates when every date is in the past.
    const openOn = this._parse([...this.dates.keys()].sort()[0]);

    this.instance = new easepick.create({
      lang: this.currentLocale,
      element: this.calendarTarget,
      inline: true,
      firstDay: 1,
      css: (picker) => this._injectCss(picker),
      setup: (picker) => {
        picker.on("view", (evt) => this._onView(evt));
      },
    });

    this.instance.gotoDate(openOn);
  }

  disconnect() {
    this.log_function("disconnect");

    if (this.instance) this.instance.destroy();
  }

  _onView(evt) {
    if (evt.detail.view === "CalendarHeader") return this._lockNavigation(evt);
    if (evt.detail.view === "CalendarDay") return this._decorate(evt);
  }

  //* Hide the prev/next arrows at the edges so the calendar can't scroll past the
  //* first or last month that contains an event date.
  _lockNavigation(evt) {
    const month = evt.detail.date.getFullYear() * 12 + evt.detail.date.getMonth();
    const prev = evt.detail.target.querySelector(".previous-button");
    const next = evt.detail.target.querySelector(".next-button");
    if (prev) prev.style.visibility = month <= this.minMonth ? "hidden" : "";
    if (next) next.style.visibility = month >= this.maxMonth ? "hidden" : "";
  }

  _decorate(evt) {
    const target = evt.detail.target;
    target.classList.remove("selected", "date-active", "date-canceled", "date-current", "past", "sunday");

    if (evt.detail.date.getDay() === 0) target.classList.add("sunday");
    if (this._isPast(evt.detail.date)) target.classList.add("past");

    const entry = this.dates.get(this._format(evt.detail.date));
    if (!entry) return;

    target.classList.add(entry.status ? "date-active" : "date-canceled");
    if (entry.current) target.classList.add("date-current");
  }

  _isPast(date) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  }

  //* Parse a "YYYY-MM-DD" key into a local Date (avoids the UTC shift of `new Date(string)`).
  _parse(key) {
    const [y, m, d] = key.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  get currentLocale() {
    switch (this.localeValue) {
      case "en":
        return "en-US";
      case "el":
        return "el-GR";
      default:
        return "en-US";
    }
  }

  _injectCss(picker) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = this.cssValue;
    picker.ui.shadowRoot.append(link);
  }

  _format(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
}
